import React, { useState } from "react";
import { useData } from "@/context/DataContext";
import { UserCog, Users, ArrowRight, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";

const Managers = () => {
    const { users, depts, ready } = useData();
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState({});

    const managers = users.filter((u) => u.role === "manager" || u.role === "super_admin");
    const deptName = (id) => depts.find((d) => d.id === id)?.name || "—";
    const managees = (mid) => users.filter((u) => u.manager_id === mid);
    const toggleExpanded = (mid) => setExpanded((e) => ({ ...e, [mid]: !e[mid] }));

    return (
        <div className="space-y-6" data-testid="managers-page">
            <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Leadership</div>
                <h1 className="font-heading text-4xl font-bold text-slate-900 tracking-tight mt-1 flex items-center gap-3">
                    <UserCog className="h-7 w-7 text-[#2EB4AC]" />
                    Managers
                </h1>
                <p className="text-slate-500 mt-1">All managers and their direct reports</p>
            </div>

            {!ready ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {[0,1,2,3].map((i) => (
                        <div key={i} className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 animate-pulse">
                            <div className="flex gap-4">
                                <div className="h-12 w-12 bg-slate-100 rounded-full" />
                                <div className="flex-1 space-y-2">
                                    <div className="h-5 w-32 bg-slate-100 rounded" />
                                    <div className="h-3 w-48 bg-slate-100 rounded" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : managers.length === 0 ? (
                <div className="border-2 border-dashed border-teal-300 bg-teal-50/30 rounded-lg p-10 text-center">
                    <UserCog className="h-8 w-8 text-[#2EB4AC] mx-auto" />
                    <p className="mt-3 font-heading font-semibold text-slate-900">No managers yet</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {managers.map((m) => {
                        const mees = managees(m.user_id);
                        const initials = (m.name || m.email || "?").split(/[\s@]/).filter(Boolean).slice(0,2).map(s=>s[0]?.toUpperCase()).join("");
                        const isAdmin = m.role === "super_admin";
                        return (
                            <div key={m.user_id} className="bg-white border border-slate-200 rounded-lg shadow-sm p-6 hover:shadow-md transition-all">
                                <div className="flex items-start gap-4">
                                    <div className="h-12 w-12 rounded-full bg-teal-50 text-[#25908A] flex items-center justify-center font-heading font-semibold border border-teal-100 overflow-hidden flex-shrink-0">
                                        {m.picture ? <img src={m.picture} alt="" className="h-full w-full object-cover" /> : initials}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between gap-2">
                                            <h3 className="font-heading text-lg font-semibold text-slate-900 truncate">{m.name}</h3>
                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${isAdmin ? "bg-purple-100 text-purple-800 border-purple-200" : "bg-blue-100 text-blue-800 border-blue-200"}`}>
                                                {isAdmin ? "Super Admin" : "Manager"}
                                            </span>
                                        </div>
                                        <div className="text-sm text-slate-500 truncate">{m.email}</div>
                                        <div className="text-xs text-slate-500 mt-1">
                                            {deptName(m.department_id)}
                                            {Array.isArray(m.managed_department_ids) && m.managed_department_ids.length > 0 && (
                                                <span className="ml-1 inline-flex flex-wrap gap-1 align-middle">
                                                    {m.managed_department_ids.map((did) => (
                                                        <span
                                                            key={did}
                                                            data-testid={`managed-dept-badge-${m.user_id}-${did}`}
                                                            title={`Also manages ${deptName(did)}`}
                                                            className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-200"
                                                        >
                                                            +{deptName(did)}
                                                        </span>
                                                    ))}
                                                </span>
                                            )}
                                        </div>

                                        <div className="mt-4 pt-4 border-t border-slate-100">
                                            <div className="flex items-center justify-between mb-3">
                                                <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold inline-flex items-center gap-1">
                                                    <Users className="h-3 w-3" />
                                                    {mees.length} Managee{mees.length!==1?"s":""}
                                                </span>
                                            </div>
                                            {mees.length === 0 ? (
                                                <div className="text-sm text-slate-500">No direct reports assigned</div>
                                            ) : (
                                                <div className="space-y-2">
                                                    {(expanded[m.user_id] ? mees : mees.slice(0, 4)).map((u) => (
                                                        <button
                                                            key={u.user_id}
                                                            onClick={() => navigate(`/employees/${u.user_id}`)}
                                                            className="w-full flex items-center justify-between gap-2 text-sm hover:bg-slate-50 rounded-md px-2 py-1.5 transition-colors"
                                                        >
                                                            <span className="inline-flex items-center gap-1.5 min-w-0">
                                                                <span className="text-slate-900 truncate">{u.name}</span>
                                                                {u.position && (
                                                                    <span
                                                                        data-testid={`managee-position-${u.user_id}`}
                                                                        className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-indigo-50 text-indigo-700 border border-indigo-100 shrink-0"
                                                                        title={`Position: ${u.position}`}
                                                                    >
                                                                        {u.position}
                                                                    </span>
                                                                )}
                                                                {u.sub_department && (
                                                                    <span
                                                                        data-testid={`managee-sub-dept-${u.user_id}`}
                                                                        className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-teal-50 text-[#25908A] border border-teal-100 shrink-0"
                                                                        title={`Sub-department: ${u.sub_department}`}
                                                                    >
                                                                        {u.sub_department}
                                                                    </span>
                                                                )}
                                                            </span>
                                                            <ArrowRight className="h-4 w-4 text-slate-400 shrink-0" />
                                                        </button>
                                                    ))}
                                                    {mees.length > 4 && (
                                                        <button
                                                            onClick={() => toggleExpanded(m.user_id)}
                                                            data-testid={`toggle-managees-${m.user_id}`}
                                                            className="w-full inline-flex items-center justify-center gap-1 text-xs text-[#25908A] hover:text-[#2EB4AC] hover:bg-teal-50/40 rounded-md px-2 py-1.5 font-medium transition-colors"
                                                        >
                                                            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded[m.user_id] ? "rotate-180" : ""}`} />
                                                            {expanded[m.user_id] ? "Show less" : `Show ${mees.length - 4} more`}
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default Managers;
